import { useState, useEffect, useMemo, Suspense, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import posthog from 'posthog-js'
import { DashboardTabBar, RoleView, ROLE_TABS } from '../shell/DashboardTabBar'
import { lazyNamed } from '../lib/lazyComponent'

// Each dashboard view is its own webpack chunk — only the active tab is fetched.
// Default Overview chunks are tiny so first-paint is fast; secondary tabs load on click.

// Admin views
const AdminOverview = lazyNamed(() => import('./views/AdminOverview'), 'AdminOverview')
const AdminTransactions = lazyNamed(() => import('./views/AdminTransactions'), 'AdminTransactions')
const AdminUsers = lazyNamed(() => import('./views/AdminUsers'), 'AdminUsers')
const AdminSystemHealth = lazyNamed(() => import('./views/AdminSystemHealth'), 'AdminSystemHealth')
const AdminAnalytics = lazyNamed(() => import('./views/AdminAnalytics'), 'AdminAnalytics')
const TeamManagementView = lazyNamed(() => import('./views/TeamManagementView'), 'TeamManagementView')
const PredictionsView = lazyNamed(() => import('./views/PredictionsView'), 'PredictionsView')
const NLQueryView = lazyNamed(() => import('./views/NLQueryView'), 'NLQueryView')
const AnomalyView = lazyNamed(() => import('./views/AnomalyView'), 'AnomalyView')
const MultiChainView = lazyNamed(() => import('./views/MultiChainView'), 'MultiChainView')
const V2ComplianceView = lazyNamed(() => import('./views/V2ComplianceView'), 'V2ComplianceView')
const V2IntelligenceView = lazyNamed(() => import('./views/V2IntelligenceView'), 'V2IntelligenceView')
const LiquidityProtocolsView = lazyNamed(() => import('./views/LiquidityProtocolsView'), 'LiquidityProtocolsView')
const LiquidityProvidersView = lazyNamed(() => import('./views/LiquidityProvidersView'), 'LiquidityProvidersView')
const AgenticApprovalsView = lazyNamed(() => import('./views/AgenticApprovalsView'), 'AgenticApprovalsView')

// User views
const UserOverview = lazyNamed(() => import('./views/UserOverview'), 'UserOverview')
const UserProtection = lazyNamed(() => import('./views/UserProtection'), 'UserProtection')
const UserWallet = lazyNamed(() => import('./views/UserWallet'), 'UserWallet')
const UserRewards = lazyNamed(() => import('./views/UserRewards'), 'UserRewards')
const UserSettings = lazyNamed(() => import('./views/UserSettings'), 'UserSettings')
const UserHistory = lazyNamed(() => import('./views/UserHistory'), 'UserHistory')
const UserNotifications = lazyNamed(() => import('./views/UserNotifications'), 'UserNotifications')
const LeaderboardView = lazyNamed(() => import('./views/LeaderboardView'), 'LeaderboardView')
const CarbonReportView = lazyNamed(() => import('./views/CarbonReportView'), 'CarbonReportView')
const V2AddressCheckView = lazyNamed(() => import('./views/V2AddressCheckView'), 'V2AddressCheckView')
const V2InsuranceView = lazyNamed(() => import('./views/V2InsuranceView'), 'V2InsuranceView')

// Builder views
const BuilderOverview = lazyNamed(() => import('./views/BuilderOverview'), 'BuilderOverview')
const BuilderBlocks = lazyNamed(() => import('./views/BuilderBlocks'), 'BuilderBlocks')
const BuilderMEVQueue = lazyNamed(() => import('./views/BuilderMEVQueue'), 'BuilderMEVQueue')
const BuilderPerformance = lazyNamed(() => import('./views/BuilderPerformance'), 'BuilderPerformance')
const V2BridgeProtectionView = lazyNamed(() => import('./views/V2BridgeProtectionView'), 'V2BridgeProtectionView')

// Trader views
const TraderOverview = lazyNamed(() => import('./views/TraderOverview'), 'TraderOverview')
const TraderTrade = lazyNamed(() => import('./views/TraderTrade'), 'TraderTrade')
const TraderPortfolio = lazyNamed(() => import('./views/TraderPortfolio'), 'TraderPortfolio')
const TraderMarket = lazyNamed(() => import('./views/TraderMarket'), 'TraderMarket')
const WhaleFlowsView = lazyNamed(() => import('./views/WhaleFlowsView'), 'WhaleFlowsView')
const V2LiquidationView = lazyNamed(() => import('./views/V2LiquidationView'), 'V2LiquidationView')

const ViewFallback = () => (
  <div className="flex items-center justify-center py-24 text-white/50">
    <div className="animate-pulse text-sm">Loading view…</div>
  </div>
)

const DEFAULT_TABS: Record<RoleView, string> = {
  Admin: 'overview',
  User: 'overview',
  Builder: 'overview',
  Trader: 'overview',
}

export function DashboardPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const userRole = (user?.role as RoleView) ?? 'User'
  // REV0721-3 / RBAC: only Admins may impersonate another roleView.
  // Non-admins are locked to their JWT role (ignores sessionStorage spoof).
  const [adminPreview, setAdminPreview] = useState<RoleView | null>(null)
  const roleView: RoleView = useMemo(() => {
    if (user?.role === 'Admin' && adminPreview) return adminPreview
    return userRole
  }, [user?.role, adminPreview, userRole])

  const allowedTabs = useMemo(
    () => ROLE_TABS[roleView]?.map((t) => t.key) ?? [],
    [roleView],
  )

  // URL ?tab= is the source of truth (MEV-570) — clamp to role-allowed keys
  const activeTab = useMemo(() => {
    const fromUrl = searchParams.get('tab')
    if (fromUrl && allowedTabs.includes(fromUrl)) return fromUrl
    return DEFAULT_TABS[roleView]
  }, [searchParams, allowedTabs, roleView])

  // Restore Admin preview from session (Admin only)
  useEffect(() => {
    const stored = sessionStorage.getItem('mev_role_view') as RoleView | null
    if (stored && user?.role === 'Admin') setAdminPreview(stored)
    else {
      setAdminPreview(null)
      if (user?.role && user.role !== 'Admin') {
        sessionStorage.removeItem('mev_role_view')
      }
    }
  }, [user])

  // Drop invalid/spoofed ?tab= for the active role
  useEffect(() => {
    const fromUrl = searchParams.get('tab')
    if (fromUrl && allowedTabs.length && !allowedTabs.includes(fromUrl)) {
      setSearchParams({ tab: DEFAULT_TABS[roleView] }, { replace: true })
    }
  }, [allowedTabs, roleView, searchParams, setSearchParams])

  const handleTabChange = useCallback((tab: string) => {
    setSearchParams({ tab }, { replace: true })
    posthog.capture('dashboard_tab_viewed', { tab, role: user?.role })
  }, [setSearchParams, user?.role])

  // Reset to default tab when role view changes (Admin preview switcher)
  const handleRoleChange = useCallback((newRole: RoleView) => {
    if (user?.role !== 'Admin') return // non-admins cannot switch
    setAdminPreview(newRole)
    setSearchParams({ tab: DEFAULT_TABS[newRole] }, { replace: true })
    sessionStorage.setItem('mev_role_view', newRole)
  }, [user?.role, setSearchParams])

  // Expose handleRoleChange globally for TopNav RoleSwitcher (MEV-570 — effect, not render body)
  useEffect(() => {
    ;(window as any).__mevSetRoleView = handleRoleChange
    ;(window as any).__mevRoleView = roleView
    return () => {
      delete (window as any).__mevSetRoleView
      delete (window as any).__mevRoleView
    }
  }, [roleView, handleRoleChange])

  const renderView = () => {
    if (roleView === 'Admin') {
      switch (activeTab) {
        case 'overview': return <AdminOverview />
        case 'transactions': return <AdminTransactions />
        case 'users': return <AdminUsers />
        case 'health': return <AdminSystemHealth />
        case 'analytics': return <AdminAnalytics />
        case 'teams': return <TeamManagementView />
        case 'predictions': return <PredictionsView />
        case 'nl-query': return <NLQueryView />
        case 'anomalies': return <AnomalyView />
        case 'multi-chain': return <MultiChainView />
        case 'whale-flows': return <WhaleFlowsView />
        case 'compliance': return <V2ComplianceView />
        case 'intelligence': return <V2IntelligenceView />
        case 'protocols': return <LiquidityProtocolsView />
        case 'liquidity': return <LiquidityProvidersView />
        case 'agentic-approvals': return <AgenticApprovalsView />
        default: return <AdminOverview />
      }
    }
    if (roleView === 'User') {
      switch (activeTab) {
        case 'overview': return <UserOverview />
        case 'protection': return <UserProtection />
        case 'wallet': return <UserWallet />
        case 'rewards': return <UserRewards />
        case 'history': return <UserHistory />
        case 'notifications': return <UserNotifications />
        case 'settings': return <UserSettings />
        case 'leaderboard': return <LeaderboardView />
        case 'carbon-report': return <CarbonReportView />
        case 'address-check': return <V2AddressCheckView />
        case 'insurance': return <V2InsuranceView />
        default: return <UserOverview />
      }
    }
    if (roleView === 'Builder') {
      switch (activeTab) {
        case 'overview': return <BuilderOverview />
        case 'blocks': return <BuilderBlocks />
        case 'queue': return <BuilderMEVQueue />
        case 'performance': return <BuilderPerformance />
        case 'bridge-protection': return <V2BridgeProtectionView />
        default: return <BuilderOverview />
      }
    }
    if (roleView === 'Trader') {
      switch (activeTab) {
        case 'overview': return <TraderOverview />
        case 'trade': return <TraderTrade />
        case 'portfolio': return <TraderPortfolio />
        case 'market': return <TraderMarket />
        case 'whale-flows': return <WhaleFlowsView />
        case 'liquidation': return <V2LiquidationView />
        case 'address-check': return <V2AddressCheckView />
        case 'protocols': return <LiquidityProtocolsView />
        case 'liquidity': return <LiquidityProvidersView />
        default: return <TraderOverview />
      }
    }
    return null
  }

  return (
    <div className="min-h-screen font-body text-white">
      <DashboardTabBar roleView={roleView} activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Suspense fallback={<ViewFallback />}>
          {renderView()}
        </Suspense>
      </div>
    </div>
  )
}
